/**
 * Card for a pair.
 */
import { Calendar, FileText, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { PairRecord } from '@/types/remote/pair.types';
import {
  canAddOneOffSession,
  deriveDisplay,
  getAddOneOffSessionTooltip,
  getArchivedPairDissociateTooltip,
  getDissociateTooltip,
} from './pair-table-utils';

/** Props for {@link PairCard}. */
interface PairCardProps {
  readonly pair: PairRecord;
  readonly onView: () => void;
  readonly onEdit?: () => void;
  readonly onDissociate?: () => void;
  readonly onViewSessions?: () => void;
  readonly onAddOneOffSession?: () => void;
}

/** Remote pair card (grid view) — same actions as {@link PairRow}. */
export function PairCard({
  pair,
  onView,
  onEdit,
  onDissociate,
  onViewSessions,
  onAddOneOffSession,
}: PairCardProps) {
  const navigate = useNavigate();
  const kpis = pair.kpis;
  const {
    isArchived,
    reportsWritten,
    reportsTotal,
    sessionsPlanned,
    sessionsCompleted,
    statusCfg,
    progCfg,
    tutorSituationCfg,
    studentSituationCfg,
    editTooltip,
  } = deriveDisplay(pair);

  return (
    <TableCard onClick={onView}>
      <div className="data-card-header">
        <div className="flex items-center justify-between gap-2 w-full">
          <LabelBadge
            label={statusCfg.label}
            icon={statusCfg.icon}
            variant={statusCfg.badgeVariant}
            color={statusCfg.badgeColor}
            size="sm"
          />
          {progCfg && (
            <LabelBadge label={progCfg.label} variant="outlined" color={progCfg.color} size="sm" />
          )}
        </div>
      </div>

      <div className="data-card-content">
        <div
          className="grid grid-cols-2 rounded-lg border p-2 mb-1.5"
          style={{ borderColor: 'color-mix(in srgb, var(--color-1-nuance-3) 40%, transparent)' }}
        >
          <div className="flex flex-col min-w-0">
            <span className="text-xs text-muted-foreground">Tuteur</span>
            <button
              type="button"
              className="font-medium text-foreground text-sm truncate hover:underline text-left"
              onClick={(e) => {
                e.stopPropagation();
                void navigate(`/tutors?view=${pair.tutorUserId}`);
              }}
            >
              {pair.tutorName}
            </button>
            {tutorSituationCfg && (
              <div className="mt-0.5">
                <LabelBadge
                  label={tutorSituationCfg.label}
                  icon={tutorSituationCfg.icon}
                  variant={tutorSituationCfg.badgeVariant}
                  color={tutorSituationCfg.badgeColor}
                  size="sm"
                />
              </div>
            )}
          </div>

          <div
            className="flex flex-col min-w-0 pl-3 border-l"
            style={{ borderColor: 'color-mix(in srgb, var(--color-1-nuance-3) 40%, transparent)' }}
          >
            <span className="text-xs text-muted-foreground">Élève</span>
            <button
              type="button"
              className="font-medium text-foreground text-sm truncate hover:underline text-left"
              onClick={(e) => {
                e.stopPropagation();
                void navigate(`/students?view=${pair.studentUserId}`);
              }}
            >
              {pair.studentName}
            </button>
            {studentSituationCfg && (
              <div className="mt-0.5">
                <LabelBadge
                  label={studentSituationCfg.label}
                  icon={studentSituationCfg.icon}
                  variant={studentSituationCfg.badgeVariant}
                  color={studentSituationCfg.badgeColor}
                  size="sm"
                />
              </div>
            )}
          </div>
        </div>

        <div className="pt-1.5 space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="inline-flex">
                    <LabelBadge
                      label={`${sessionsCompleted}/${sessionsPlanned} séances`}
                      icon={Calendar}
                      variant="ghost"
                      color="var(--color-2-nuance-1)"
                      size="sm"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-xs space-y-1">
                    <p>
                      Séances réalisées / planifiées : {sessionsCompleted}/{sessionsPlanned}
                    </p>
                    {kpis && <p>Séances annulées : {kpis.sessionsCancelled}</p>}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="inline-flex">
                    <LabelBadge
                      label={`${reportsWritten}/${reportsTotal} CR`}
                      icon={FileText}
                      variant="ghost"
                      color="var(--color-1-nuance-1)"
                      size="sm"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-xs space-y-1">
                    <p>
                      Comptes rendus rédigés : {reportsWritten}/{reportsTotal}
                    </p>
                    {kpis?.averageScore !== null && kpis?.averageScore !== undefined && (
                      <p>Note moyenne : {kpis.averageScore.toFixed(1)}/5</p>
                    )}
                    {pair.managedBy && <p>Géré par {pair.managedBy.fullName}</p>}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView();
            }}
          />

          {onEdit && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              disabled={isArchived}
              tooltipText={editTooltip}
            />
          )}

          {onViewSessions && (
            <Button
              preset="row-view-sessions"
              onClick={(e) => {
                e.stopPropagation();
                onViewSessions();
              }}
            />
          )}

          {onAddOneOffSession && (
            <Button
              preset="row-add-one-off-session"
              onClick={(e) => {
                e.stopPropagation();
                onAddOneOffSession();
              }}
              disabled={!canAddOneOffSession(pair)}
              tooltipText={getAddOneOffSessionTooltip(pair)}
            />
          )}

          {onDissociate &&
            (isArchived ? (
              <Button
                preset="row-delete"
                icon={XCircle}
                disabled
                tooltipText={getArchivedPairDissociateTooltip()}
              />
            ) : (
              <Button
                preset="row-delete"
                icon={XCircle}
                tooltipText={getDissociateTooltip()}
                onClick={(e) => {
                  e.stopPropagation();
                  onDissociate();
                }}
              />
            ))}
        </div>
      </div>
    </TableCard>
  );
}
